import React, { useState, useEffect } from 'react';
import apiService, { uploadsBaseUrl } from '../api/apiService';
import './TablePages.css';

const resolveImageSrc = (url: string | undefined) =>
  url && !url.startsWith('http') ? `${uploadsBaseUrl}${url.startsWith('/') ? url : '/' + url}` : url;

interface BannerRow {
  id: number;
  title: string | null;
  image_url: string;
  link_url: string | null;
  is_active: boolean;
  display_order: number;
}

const Banners: React.FC = () => {
  const [list, setList] = useState<BannerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formTitle, setFormTitle] = useState('');
  const [formImageUrl, setFormImageUrl] = useState('');
  const [formLinkUrl, setFormLinkUrl] = useState('');
  const [formActive, setFormActive] = useState(true);
  const [formDisplayOrder, setFormDisplayOrder] = useState(0);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiService.get('/admin/banners');
      setList(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openAdd = () => {
    setEditingId(null);
    setFormTitle('');
    setFormImageUrl('');
    setFormLinkUrl('');
    setFormActive(true);
    setFormDisplayOrder(list.length > 0 ? Math.max(...list.map((b) => b.display_order), 0) + 1 : 0);
    setShowForm(true);
  };

  const openEdit = (row: BannerRow) => {
    setEditingId(row.id);
    setFormTitle(row.title || '');
    setFormImageUrl(row.image_url || '');
    setFormLinkUrl(row.link_url || '');
    setFormActive(row.is_active);
    setFormDisplayOrder(row.display_order);
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formImageUrl.trim()) {
      alert('Image URL is required.');
      return;
    }
    try {
      const payload = {
        title: formTitle.trim() || undefined,
        image_url: formImageUrl.trim(),
        link_url: formLinkUrl.trim() || undefined,
        is_active: formActive,
        display_order: formDisplayOrder,
      };
      if (editingId) {
        await apiService.put(`/admin/banners/${editingId}`, payload);
      } else {
        await apiService.post('/admin/banners', payload);
      }
      setShowForm(false);
      load();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this banner?')) return;
    try {
      await apiService.delete(`/admin/banners/${id}`);
      load();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed');
    }
  };

  const handleUploadImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Please select an image (JPEG, PNG, GIF, WebP).');
      return;
    }
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await apiService.post('/upload/profile-image', formData, {
        transformRequest: [(data: unknown, headers: Record<string, string>) => {
          delete headers['Content-Type'];
          return data;
        }],
      });
      const relativeUrl = (data && (data as { relative_url?: string }).relative_url) ? (data as { relative_url: string }).relative_url : '';
      const url = (data && (data as { url?: string }).url) ? (data as { url: string }).url : '';
      if (relativeUrl) setFormImageUrl(relativeUrl);
      else if (url) setFormImageUrl(url);
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  return (
    <div className="table-page">
      <div className="table-toolbar">
        <div className="table-toolbar-right">
          <button type="button" className="btn-primary" onClick={openAdd}>Add banner</button>
          <button type="button" onClick={load} className="btn-primary">Refresh</button>
        </div>
      </div>
      {loading ? (
        <p>Loading...</p>
      ) : (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Order</th>
                <th>Title</th>
                <th>Image</th>
                <th>Link</th>
                <th>Active</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((row) => (
                <tr key={row.id}>
                  <td>{row.display_order}</td>
                  <td>{row.title || '-'}</td>
                  <td>
                    {row.image_url ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <img
                          src={resolveImageSrc(row.image_url)}
                          alt=""
                          style={{ maxWidth: 80, maxHeight: 50, objectFit: 'cover', borderRadius: 4, verticalAlign: 'middle' }}
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                        <a href={resolveImageSrc(row.image_url)} target="_blank" rel="noopener noreferrer">View</a>
                      </span>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>{row.link_url ? <a href={row.link_url} target="_blank" rel="noopener noreferrer">Link</a> : '-'}</td>
                  <td>{row.is_active ? 'Yes' : 'No'}</td>
                  <td>
                    <div className="table-actions">
                      <button type="button" className="btn-sm" onClick={() => openEdit(row)}>Edit</button>
                      <button type="button" className="btn-sm red" onClick={() => handleDelete(row.id)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h3>{editingId ? 'Edit' : 'Add'} banner</h3>
            <form onSubmit={handleSubmit}>
              <label>Title (optional)</label>
              <input
                type="text"
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                className="input-field"
                placeholder="e.g. Special Offer"
              />
              <label>Banner image *</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <input type="file" ref={fileInputRef} accept="image/*" onChange={handleUploadImage} style={{ display: 'none' }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <button type="button" className="btn-secondary" onClick={() => fileInputRef.current?.click()} disabled={uploadingImage}>
                    {uploadingImage ? 'Uploading...' : 'Upload image'}
                  </button>
                  {formImageUrl && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <img src={resolveImageSrc(formImageUrl)} alt="" style={{ maxWidth: 80, maxHeight: 50, objectFit: 'cover', borderRadius: 4 }} onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                      <button type="button" className="btn-sm" onClick={() => setFormImageUrl('')}>Remove</button>
                    </span>
                  )}
                </div>
                <input
                  type="text"
                  value={formImageUrl}
                  onChange={(e) => setFormImageUrl(e.target.value)}
                  className="input-field"
                  placeholder="Or paste image URL (e.g. /uploads/... or https://...)"
                  required
                />
              </div>
              <label>Link URL (optional)</label>
              <input
                type="url"
                value={formLinkUrl}
                onChange={(e) => setFormLinkUrl(e.target.value)}
                className="input-field"
                placeholder="https://..."
              />
              <label>Display order</label>
              <input
                type="number"
                min={0}
                value={formDisplayOrder}
                onChange={(e) => setFormDisplayOrder(parseInt(e.target.value, 10) || 0)}
                className="input-field"
              />
              <label>
                <input type="checkbox" checked={formActive} onChange={(e) => setFormActive(e.target.checked)} />
                Active (shown in app)
              </label>
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="btn-primary">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Banners;
